import { useEffect, useState, useCallback } from "react";

import {
  Paper,
  TableBody,
  TableContainer,
  TableHead,
  TableRow,
  CircularProgress,
  Box,
  Typography,
} from "@mui/material";
import { useRedirect } from "react-admin";

// Common styles
import {
  CustomTableRowTR,
  FilledButton,
  StyledTable,
  TableData,
  TableHeader,
  TableTopHeader,
  TableWrapper,
  Title,
} from "../../styles/commonStyles";

// Services
import { DishCategoryTagService } from "../../services/DishCategoryTagService";

interface Props {
  tagId: number;
}

const dishCategoryTagService = new DishCategoryTagService();

const IMAGE_STYLES = {
  width: 80,
  height: 60,
  minWidth: 80,
  minHeight: 60,
  maxWidth: 80,
  maxHeight: 60,
  borderRadius: "6px",
  objectFit: "cover" as const,
  border: "1px solid #e0e0e0",
  display: "block",
};

const DishCategoryTagDishesTab = ({ tagId }: Props) => {
  const [dishes, setDishes] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const redirect = useRedirect();

  const fetchDishes = useCallback(async () => {
    if (!tagId) return;

    setLoading(true);
    setError(null);

    try {
      const response = await dishCategoryTagService.getDishesForTag(tagId);
      setDishes(response ?? []);
    } catch {
      setError("Failed to load dishes");
    } finally {
      setLoading(false);
    }
  }, [tagId]);

  useEffect(() => {
    fetchDishes();
  }, [fetchDishes]);

  const isInitialLoading = loading && dishes.length === 0;

  return (
    <TableWrapper>
      <TableTopHeader>
        <Title>Dishes in this Tag</Title>
      </TableTopHeader>

      {isInitialLoading && (
        <Box display="flex" justifyContent="center" py={4}>
          <CircularProgress size={28} />
        </Box>
      )}

      {!loading && error && dishes.length === 0 && (
        <Box py={4} textAlign="center">
          <Typography color="error">{error}</Typography>
        </Box>
      )}

      {!isInitialLoading && !error && dishes.length === 0 && (
        <Box py={4} textAlign="center">
          <Typography color="text.secondary">
            No dishes found for this tag
          </Typography>
        </Box>
      )}

      {dishes.length > 0 && (
        <TableContainer component={Paper} sx={{ borderRadius: 2 }}>
          <StyledTable
            sx={{
              width: "100%",
              tableLayout: "fixed",
            }}
          >
            <TableHead>
              <TableRow
                sx={{
                  backgroundColor: "rgba(232,235,237,0.3) !important",
                }}
              >
                <TableHeader sx={{ width: 70 }}>Id</TableHeader>

                <TableHeader>Name</TableHeader>

                <TableHeader sx={{ width: 120 }}>
                  Meal Type
                </TableHeader>

                <TableHeader sx={{ width: 100 }}>
                  Type
                </TableHeader>

                <TableHeader sx={{ width: 160 }}>
                  Category
                </TableHeader>

                <TableHeader sx={{ width: 120 }}>
                  Image
                </TableHeader>

                <TableHeader sx={{ width: 120 }}>
                  Action
                </TableHeader>
              </TableRow>
            </TableHead>

            <TableBody>
              {dishes.map((row: any) => (
                <CustomTableRowTR key={row.id}>
                  <TableData>{row.id || "-"}</TableData>

                  <TableData>{row.name || "-"}</TableData>

                  <TableData>{row.mealType || "-"}</TableData>

                  <TableData>{row.type || "-"}</TableData>

                  <TableData>{row?.category?.name || "-"}</TableData>

                  <TableData
                    sx={{
                      width: 120,
                    }}
                  >
                    <Box
                      sx={{
                        width: 80,
                        height: 60,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        mx: "auto",
                      }}
                    >
                      {row.image ? (
                        <Box
                          component="img"
                          src={row.image}
                          alt={row.name}
                          loading="lazy"
                          sx={IMAGE_STYLES}
                          onError={(e: any) => {
                            e.target.onerror = null;
                            e.target.src =
                              "/images/image-placeholder.png";
                          }}
                        />
                      ) : (
                        <Typography
                          variant="body2"
                          color="text.secondary"
                        >
                          N/A
                        </Typography>
                      )}
                    </Box>
                  </TableData>

                  <TableData
                    sx={{
                      width: 120,
                    }}
                  >
                    <FilledButton
                      size="small"
                      variant="text"
                      onClick={() =>
                        redirect("show", "CuisineDish", row.id)
                      }
                    >
                      View Dish
                    </FilledButton>
                  </TableData>
                </CustomTableRowTR>
              ))}
            </TableBody>
          </StyledTable>
        </TableContainer>
      )}
    </TableWrapper>
  );
};

export default DishCategoryTagDishesTab;